import React, { useState, useMemo, useEffect } from 'react';
import { List } from "@refinedev/antd";
import { Table, Button, Input, Select, DatePicker, Tag, Row, Col, Card, message, Spin } from "antd";
import { ClearOutlined, SearchOutlined, EyeOutlined, DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { AdjacentCrop, AdjacentCropStatus, CropSpecies, CropVariety } from "../../types/adjacent-crop";
import { adjacentCropService } from "../../services/api/adjacentCropService";
import dayjs, { Dayjs } from "dayjs";
import "./adjacent-crops.css";
import { usePageTitle } from "../../hooks";

const { RangePicker } = DatePicker;

/**
 * AdjacentCropList Component
 * 
 * Displays a list of all adjacent crops with filtering, search, sorting, and pagination.
 * 
 * Features:
 * - Table view with all relevant columns
 * - Filters: species, variety, producer, farm, status, date range
 * - Search functionality
 * - Pagination
 * - Column sorting
 * - Actions: view detail, edit, close
 * 
 * Requirements: 10.1, 10.2, 10.3, 10.4
 */
export const AdjacentCropList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Set page title
  usePageTitle(t("adjacent_crops.title", "Cultivos Adyacentes"));

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<AdjacentCropStatus | undefined>();
  const [speciesFilter, setSpeciesFilter] = useState<string | undefined>();
  const [varietyFilter, setVarietyFilter] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  // Data states
  const [crops, setCrops] = useState<AdjacentCrop[]>([]);
  const [total, setTotal] = useState(0);
  const [species, setSpecies] = useState<CropSpecies[]>([]);
  const [varieties, setVarieties] = useState<CropVariety[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSpecies, setLoadingSpecies] = useState(false);
  const [loadingVarieties, setLoadingVarieties] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Load species on mount
  useEffect(() => {
    loadSpecies();
  }, []);

  // Load varieties when species filter changes
  useEffect(() => {
    if (speciesFilter) {
      loadVarietiesForSpecies(speciesFilter);
    } else {
      setVarieties([]);
      setVarietyFilter(undefined);
    }
  }, [speciesFilter]);

  // Debounced search text
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  // Debounce search text changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500); // Wait 500ms after user stops typing

    return () => {
      clearTimeout(timer);
    };
  }, [searchText]);

  // Load data when filters or pagination changes
  useEffect(() => {
    loadData();
  }, [currentPage, pageSize, statusFilter, speciesFilter, varietyFilter, debouncedSearchText, dateRange]);

  /**
   * Load adjacent crops with server-side pagination and filtering
   */
  const loadData = async () => {
    setLoading(true);
    try {
      const params: any = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };
      
      if (statusFilter) {
        params.status = statusFilter;
      }
      
      if (speciesFilter) {
        params.species = speciesFilter;
      }
      
      if (varietyFilter) {
        params.variety = varietyFilter;
      }
      
      if (debouncedSearchText && debouncedSearchText.trim()) {
        params.search = debouncedSearchText.trim();
      }
      
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.date_from = dateRange[0].format('YYYY-MM-DD');
        params.date_to = dateRange[1].format('YYYY-MM-DD');
      }

      // Build query string
      const queryString = new URLSearchParams(
        Object.entries(params).map(([key, value]) => [key, String(value)])
      ).toString();

      // Make API call with pagination using apiClient
      const { apiClient } = await import('../../services/api/apiClient');
      const response = await apiClient.get(`/adjacent-crops/?${queryString}`);
      
      // Check if response is paginated (has results and count) or direct array
      if (response.data.results !== undefined) {
        // Paginated response
        setCrops(response.data.results);
        setTotal(response.data.count);
      } else if (Array.isArray(response.data)) {
        // Direct array response - backend not using pagination
        setCrops(response.data);
        setTotal(response.data.length);
      } else {
        // Unexpected format
        console.error('Unexpected API response format:', response.data);
        setCrops([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error loading adjacent crops:', error);
      message.error(t("adjacent_crops.load_error", "Error al cargar cultivos adyacentes"));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load all crop species
   */
  const loadSpecies = async () => {
    setLoadingSpecies(true);
    try {
      // Load all species without pagination by using a large limit
      const { apiClient } = await import('../../services/api/apiClient');
      const response = await apiClient.get('/crop-species/?limit=1000');
      
      let allSpecies = [];
      if (response.data.results !== undefined) {
        allSpecies = response.data.results;
      } else if (Array.isArray(response.data)) {
        allSpecies = response.data;
      }
      
      setSpecies(allSpecies.filter((s: any) => s.is_active));
    } catch (error) {
      console.error('Error loading species:', error);
    } finally {
      setLoadingSpecies(false);
    }
  };

  /**
   * Load varieties for selected species
   */
  const loadVarietiesForSpecies = async (speciesId: string) => {
    setLoadingVarieties(true);
    try {
      // Load all varieties for the species without pagination
      const { apiClient } = await import('../../services/api/apiClient');
      const response = await apiClient.get(`/crop-species/${speciesId}/varieties/?limit=1000`);
      
      let allVarieties = [];
      if (response.data.varieties !== undefined) {
        allVarieties = response.data.varieties;
      } else if (response.data.results !== undefined) {
        allVarieties = response.data.results;
      } else if (Array.isArray(response.data)) {
        allVarieties = response.data;
      }
      
      setVarieties(allVarieties.filter((v: any) => v.is_active));
    } catch (error) {
      console.error('Error loading varieties:', error);
      setVarieties([]);
    } finally {
      setLoadingVarieties(false);
    }
  };



  /**
   * Navigate to detail page
   */
  const handleViewDetail = (record: AdjacentCrop) => {
    navigate(`/adjacent-crops/${record.id}`);
  };

  /**
   * Get status color
   */
  const getStatusColor = (status: AdjacentCropStatus): string => {
    return status === "open" ? "green" : "red";
  };

  /**
   * Get status text
   */
  const getStatusText = (status: AdjacentCropStatus): string => {
    return status === "open" 
      ? t("adjacent_crops.status.open", "Abierto") 
      : t("adjacent_crops.status.closed", "Cerrado");
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setSearchText("");
    setStatusFilter(undefined);
    setSpeciesFilter(undefined);
    setVarietyFilter(undefined);
    setDateRange(null);
    setCurrentPage(1); // Reset to first page
  };

  /**
   * Handle pagination change
   */
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = useMemo(() => {
    return !!(debouncedSearchText || statusFilter || speciesFilter || varietyFilter || dateRange);
  }, [debouncedSearchText, statusFilter, speciesFilter, varietyFilter, dateRange]);

  /**
   * Handle Excel export
   */
  const handleExportToExcel = async () => {
    setExportingExcel(true);
    try {
      // Build export filters
      const exportFilters: any = {};
      
      if (statusFilter) {
        exportFilters.status = statusFilter;
      }
      
      if (speciesFilter) {
        exportFilters.species_id = speciesFilter;
      }
      
      if (dateRange && dateRange[0] && dateRange[1]) {
        exportFilters.date_from = dateRange[0].format('YYYY-MM-DD');
        exportFilters.date_to = dateRange[1].format('YYYY-MM-DD');
      }
      
      // Call export service
      const blob = await adjacentCropService.exportToExcel(exportFilters);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cultivos_adyacentes_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(t("adjacent_crops.export.success", "Archivo Excel exportado exitosamente"));
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      message.error(t("adjacent_crops.export.error", "Error al exportar archivo Excel"));
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <div className="adjacent-crops-list">
      <List>
        {/* Filters Section */}
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            {/* Search */}
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input.Search
                placeholder={t("adjacent_crops.search_placeholder", "Buscar por productor, finca...")}
                allowClear
                enterButton={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>

            {/* Status Filter */}
            <Col xs={24} sm={12} md={8} lg={4}>
              <Select
                placeholder={t("adjacent_crops.filter.status", "Estado")}
                allowClear
                style={{ width: '100%' }}
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { label: t("adjacent_crops.status.open", "Abierto"), value: "open" },
                  { label: t("adjacent_crops.status.closed", "Cerrado"), value: "closed" },
                ]}
              />
            </Col>

            {/* Species Filter */}
            <Col xs={24} sm={12} md={8} lg={5}>
              <Select
                placeholder={t("adjacent_crops.filter.species", "Especie")}
                allowClear
                showSearch
                style={{ width: '100%' }}
                value={speciesFilter}
                onChange={setSpeciesFilter}
                loading={loadingSpecies}
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                options={species.map(s => ({
                  label: s.name,
                  value: s.id,
                }))}
              />
            </Col>

            {/* Variety Filter */}
            <Col xs={24} sm={12} md={8} lg={5}>
              <Select
                placeholder={t("adjacent_crops.filter.variety", "Variedad")}
                allowClear
                showSearch
                style={{ width: '100%' }}
                value={varietyFilter}
                onChange={setVarietyFilter}
                loading={loadingVarieties}
                disabled={!speciesFilter}
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                options={varieties.map(v => ({
                  label: v.name,
                  value: v.id,
                }))}
              />
            </Col>

            {/* Date Range Filter */}
            <Col xs={24} sm={12} md={8} lg={4}>
              <RangePicker
                style={{ width: '100%' }}
                value={dateRange}
                onChange={setDateRange}
                format="DD/MM/YYYY"
                placeholder={[
                  t("adjacent_crops.filter.date_from", "Desde"),
                  t("adjacent_crops.filter.date_to", "Hasta")
                ]}
              />
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row style={{ marginTop: 16 }} gutter={[8, 8]}>
            {hasActiveFilters && (
              <Col>
                <Button
                  icon={<ClearOutlined />}
                  onClick={handleClearFilters}
                  type="default"
                >
                  {t("common.clear_filters", "Limpiar filtros")}
                </Button>
              </Col>
            )}
            <Col>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportToExcel}
                loading={exportingExcel}
                type="primary"
              >
                {t("adjacent_crops.export.button", "Exportar a Excel")}
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Table */}
        {loading ? (
          <Card>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                {t("common.loading", "Cargando...")}
              </div>
            </div>
          </Card>
        ) : (
          <Table 
              dataSource={crops}
              rowKey="id" 
              scroll={{ x: 1600 }}
              loading={loading}
              onChange={handleTableChange}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: total,
                showSizeChanger: true,
                showTotal: (total) => t("common.total_items", `Total: ${total} elementos`),
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
            >
          <Table.Column
            dataIndex="species_name"
            title={t("adjacent_crops.field.species", "Especie")}
            width={150}
          />
          <Table.Column
            dataIndex="variety_name"
            title={t("adjacent_crops.field.variety", "Variedad")}
            width={150}
            render={(value: string) => value || t("adjacent_crops.variety.unknown", "Desconocida")}
          />
          <Table.Column
            dataIndex="total_estimated_surface"
            title={t("adjacent_crops.field.surface", "Superficie (ha)")}
            width={130}
            align="center"
            render={(value: number) => value ? value.toFixed(2) : '-'}
          />
          <Table.Column
            dataIndex="status"
            title={t("adjacent_crops.field.status", "Estado")}
            width={100}
            render={(value: AdjacentCropStatus) => (
              <Tag color={getStatusColor(value)}>
                {getStatusText(value)}
              </Tag>
            )}
          />
          <Table.Column
            dataIndex="producer_name"
            title={t("adjacent_crops.field.producer", "Productor")}
            width={200}
          />
          <Table.Column
            dataIndex="farm_name"
            title={t("adjacent_crops.field.farm", "Finca")}
            width={200}
          />
          <Table.Column
            dataIndex="farm_municipality"
            title={t("adjacent_crops.field.municipality", "Municipio")}
            width={150}
          />
          <Table.Column
            dataIndex="detection_date"
            title={t("adjacent_crops.field.detection_date", "Fecha Detección")}
            width={140}
            render={(value: string) => value ? dayjs(value).format("DD/MM/YYYY") : '-'}
          />

          <Table.Column
            title={t("common.actions", "Acciones")}
            dataIndex="actions"
            width={80}
            fixed="right"
            render={(_, record: AdjacentCrop) => (
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewDetail(record)}
                title={t("common.view", "Ver")}
              />
            )}
          />
          </Table>
        )}
      </List>
    </div>
  );
};

export default AdjacentCropList;
